import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FiUserPlus,
    FiSearch,
    FiX,
    FiTrash2,
    FiCheckCircle,
    FiClock,
    FiRefreshCw,
    FiEdit,
    FiRepeat
} from 'react-icons/fi';
import { getAllDoctors, deleteDoctor, getDoctorsByMultipleGuilds } from './doctorService';
import { getAllGuilds } from '../guilds/guildService';
import { getAllDepartments, getSpecialtiesByDepartment } from '../specialties/taxonomyService';
import { useNavigate } from 'react-router-dom';
import DoctorDetail from './DoctorDetail';
import './DoctorList.css';

export default function DoctorList({ restrictedGuilds = null, readOnly = false }) {
    const [doctors, setDoctors] = useState([]);
    const [filteredDoctors, setFilteredDoctors] = useState([]);
    const [guilds, setGuilds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDoctor, setSelectedDoctor] = useState(null);

    // Filter states
    const [filters, setFilters] = useState({
        guildId: '',
        departmentId: '',
        specialtyId: '',
        search: '',
    });
    const [departments, setDepartments] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const navigate = useNavigate();

    const loadData = React.useCallback(async () => {
        try {
            setLoading(true);
            let doctorsData;
            if (restrictedGuilds && restrictedGuilds.length > 0) {
                doctorsData = await getDoctorsByMultipleGuilds(restrictedGuilds);
            } else {
                doctorsData = await getAllDoctors();
            }
            let guildsData = await getAllGuilds();

            setDoctors(doctorsData);
            setGuilds(guildsData);

            const deptsData = await getAllDepartments();
            setDepartments(deptsData);
            setError('');
        } catch (err) {
            setError('Failed to load data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [restrictedGuilds]);

    const applyFilters = React.useCallback(() => {
        let filtered = [...doctors];

        // Filter by guild
        if (filters.guildId) {
            filtered = filtered.filter(d => d.guildId === filters.guildId);
        }

        // Filter by department
        if (filters.departmentId) {
            filtered = filtered.filter(d => d.departmentId === filters.departmentId);
        }

        // Filter by specialty
        if (filters.specialtyId) {
            filtered = filtered.filter(d => d.specialtyId === filters.specialtyId);
        }

        // Filter by search (fullName, email, or ahgId)
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(d =>
                (d.fullName?.toLowerCase().includes(searchLower)) ||
                (d.email?.toLowerCase().includes(searchLower)) ||
                (d.ahgId?.toLowerCase().includes(searchLower))
            );
        }

        setFilteredDoctors(filtered);
    }, [doctors, filters]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    async function handleFilterChange(e) {
        const { name, value } = e.target;

        if (name === 'departmentId') {
            setFilters(prev => ({ ...prev, [name]: value, specialtyId: '' }));
            if (value) {
                const specs = await getSpecialtiesByDepartment(value);
                setSpecialties(specs);
            } else {
                setSpecialties([]);
            }
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    }

    function clearFilters() {
        setFilters({
            guildId: '',
            departmentId: '',
            specialtyId: '',
            search: '',
        });
        setSpecialties([]);
    }

    async function handleDelete(id, event) {
        event.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this doctor?')) {
            return;
        }

        try {
            await deleteDoctor(id);
            await loadData();
        } catch (err) {
            alert('Failed to delete doctor');
            console.error(err);
        }
    }

    function handleRowClick(doctor) {
        setSelectedDoctor(doctor);
    }

    function handleEdit(id, event) {
        if (event) event.stopPropagation();
        navigate(`/dashboard/doctors/edit/${id}`);
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading doctors...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <h1>Doctors</h1>
                </div>
                {!readOnly && (
                    <Link to="/dashboard/doctors/create" className="btn-primary">
                        <FiUserPlus />
                        <span>Add Doctor</span>
                    </Link>
                )}
            </div>

            {error && (
                <div className="error-message">
                    <FiX />
                    {error}
                </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        name="search"
                        placeholder="Search by name, email, or AHG ID..."
                        value={filters.search}
                        onChange={handleFilterChange}
                    />
                </div>

                <div className="filter-group">
                    <select
                        name="guildId"
                        value={filters.guildId}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Guilds</option>
                        {guilds.map(guild => (
                            <option key={guild.id} value={guild.id}>{guild.name}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <select
                        name="departmentId"
                        value={filters.departmentId}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Departments</option>
                        {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <select
                        name="specialtyId"
                        value={filters.specialtyId}
                        onChange={handleFilterChange}
                        disabled={!filters.departmentId}
                    >
                        <option value="">All Specialties</option>
                        {specialties.map(spec => (
                            <option key={spec.id} value={spec.id}>{spec.name}</option>
                        ))}
                    </select>
                </div>

                {(filters.guildId || filters.departmentId || filters.specialtyId || filters.search) && (
                    <button onClick={clearFilters} className="btn-clear-filters">
                        <FiX />
                        <span>Clear Filters</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Guild</th>
                            <th>Specialty</th>
                            <th>Department</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDoctors.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="empty-state">
                                    <div className="empty-content">
                                        <FiUserPlus className="empty-icon" />
                                        <p>
                                            {filters.guildId || filters.departmentId || filters.specialtyId || filters.search
                                                ? 'No doctors match your filters.'
                                                : 'No doctors found. Create your first doctor.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredDoctors.map((doctor) => {
                                const guild = guilds.find(g => g.id === doctor.guildId);
                                return (
                                    <tr
                                        key={doctor.id}
                                        onClick={() => handleRowClick(doctor)}
                                        className="clickable-row"
                                    >
                                        <td className="name-cell">
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <strong>{doctor.fullName || 'N/A'}</strong>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>{doctor.email}</span>
                                                {doctor.ahgId && (
                                                    <span style={{ fontSize: '11px', color: '#6366f1', fontWeight: 600, marginTop: '2px' }}>{doctor.ahgId}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{guild?.name || 'N/A'}</td>
                                        <td>{doctor.specialtyName || doctor.specialty || 'N/A'}</td>
                                        <td>{doctor.departmentName || 'N/A'}</td>
                                        <td>{doctor.phone || 'N/A'}</td>
                                        <td>
                                            <span className={`status-badge ${doctor.profileCompleted ? 'completed' : 'pending'}`}>
                                                {doctor.profileCompleted ? (
                                                    <>
                                                        <FiCheckCircle />
                                                        <span>Completed</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiClock />
                                                        <span>Pending</span>
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            {!readOnly ? (
                                                <>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/referrals?prefilledDoctor=${doctor.id || doctor.uid}`); }}
                                                        className="btn-edit"
                                                        title="Refer a patient to this doctor"
                                                        style={{ color: '#6366f1' }}
                                                    >
                                                        <FiRepeat />
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleEdit(doctor.id, e)}
                                                        className="btn-edit"
                                                        title="Edit doctor"
                                                    >
                                                        <FiEdit />
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleDelete(doctor.id, e)}
                                                        className="btn-delete"
                                                        title="Delete doctor"
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </>
                                            ) : (
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button
                                                        className="btn-edit"
                                                        title="Refer a patient to this doctor"
                                                        style={{ color: '#6366f1', padding: '6px 10px' }}
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/referrals?prefilledDoctor=${doctor.id || doctor.uid}`); }}
                                                    >
                                                        <FiRepeat />
                                                    </button>
                                                    <button
                                                        className="btn-secondary"
                                                        style={{ padding: '6px 12px' }}
                                                        onClick={() => handleRowClick(doctor)}
                                                    >
                                                        View Details
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Doctor Detail Modal */}
            {selectedDoctor && (
                <DoctorDetail
                    doctor={selectedDoctor}
                    guild={guilds.find(g => g.id === selectedDoctor.guildId)}
                    onClose={() => setSelectedDoctor(null)}
                    onEdit={handleEdit}
                />
            )}
        </div>
    );
}
